/**
 * Shared reference data — books, translators, translator relationships,
 * witnesses and witness types — loaded on demand and shared by every screen.
 *
 * None of it is persisted, and it used to be fetched ad hoc by whichever
 * component needed a piece: the dashboard loaded everything, each lemma-wizard
 * step loaded only its own slice. Opening a step directly (a refresh, a
 * bookmark, a resumed draft) left its dropdowns empty — the translator
 * relationship select offered only "—" — so what a contributor could pick
 * depended on how they had navigated there. Components mounting together also
 * fetched the same list twice.
 *
 * Callers now ask for the kinds they need. A kind is fetched only while the
 * store has none of it, and at most one request per kind is in flight; every
 * caller asking meanwhile shares that request. A failed load leaves the store
 * empty, so the next caller retries.
 */

import { ActionContext } from 'vuex'
import { HexaplaState } from '@/store/state'
import { useApiUrl } from '@/compositions/servicebuilder/useApiUrl'
import { useBooksService } from '@/compositions/services/useBooksService'
import { useTranslatorsService } from '@/compositions/services/useTranslatorsService'
import { useWitnessesService } from '@/compositions/services/useWitnessesService'

export type ReferenceKind = 'books' | 'translators' | 'translatorRelationships' | 'witnesses' | 'witnessTypes'

interface Loader {
  fetch: (url: string) => Promise<unknown[]>
  mutation: string
}

// How to fetch each kind, and the mutation that stores it.
const loaders: Record<ReferenceKind, Loader> = {
  books: { fetch: (url) => useBooksService(url).listBooks(), mutation: 'SET_BOOKS' },
  translators: { fetch: (url) => useTranslatorsService(url).listTranslators(), mutation: 'SET_TRANSLATORS' },
  translatorRelationships: {
    fetch: (url) => useTranslatorsService(url).listTranslatorRelationships(),
    mutation: 'SET_TRANSLATOR_RELATIONSHIPS',
  },
  witnesses: { fetch: (url) => useWitnessesService(url).listWitnesses(), mutation: 'SET_WITNESSES' },
  witnessTypes: { fetch: (url) => useWitnessesService(url).listWitnessTypes(), mutation: 'SET_WITNESS_TYPES' },
}

export const REFERENCE_KINDS = Object.keys(loaders) as ReferenceKind[]

/** The kinds the lemma wizard's steps draw on. */
export const WIZARD_REFERENCE_KINDS: ReferenceKind[] = ['translators', 'translatorRelationships', 'witnesses', 'witnessTypes']

/** Failures by kind. A kind that loaded, or was already present, is absent. */
export type ReferenceLoadErrors = Partial<Record<ReferenceKind, unknown>>

const inFlight: Partial<Record<ReferenceKind, Promise<void>>> = {}

const load = (kind: ReferenceKind, commit: ActionContext<HexaplaState, HexaplaState>['commit']): Promise<void> => {
  const pending = inFlight[kind]
  if (pending) return pending

  const loader = loaders[kind]
  const request = loader
    .fetch(useApiUrl())
    .then((rows) => {
      commit(loader.mutation, rows)
    })
    .finally(() => {
      delete inFlight[kind]
    })

  inFlight[kind] = request
  return request
}

/**
 * Loads each requested kind the store does not yet hold.
 *
 * Never rejects: resolves to the failures by kind, so a screen can report the
 * one list that failed without losing the others.
 */
export async function ensureReferenceData(
  { state, commit }: ActionContext<HexaplaState, HexaplaState>,
  kinds: ReferenceKind[] = REFERENCE_KINDS
): Promise<ReferenceLoadErrors> {
  const errors: ReferenceLoadErrors = {}

  await Promise.all(
    kinds.map(async (kind) => {
      if ((state[kind] as unknown[]).length) return
      try {
        await load(kind, commit)
      } catch (e) {
        errors[kind] = e
      }
    })
  )

  return errors
}
